import { useEffect, useState } from 'react'
import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { useSelector } from '@/libs/state'
import { useIntlayer } from 'react-intlayer'
import type { Message } from '../domain/chat'
import { useActiveThread } from '../hooks/use-active-thread'
import { useChatActions } from '../hooks/use-chat-actions'
import { useChatActor } from '../hooks/use-chat-actor'
import { useConversations } from '../hooks/use-conversations'
import { useReplyTarget } from '../hooks/use-reply-target'
import { activeTopicId$, replyingTo$ } from '../store'
import { ConversationList } from './conversation-list'
import { ConversationMembersSheet } from './conversation-members-sheet'
import { MessageComposer } from './message-composer'
import { MessageThread } from './message-thread'
import { NewConversationDialog } from './new-conversation-dialog'
import { topicTitle } from './topic-display'
import { Input } from '@journ/ui/input'

type ChatPageProps = {
	initialTopicId?: string | null
}

const ChatPage = ({ initialTopicId }: ChatPageProps) => {
	const content = useIntlayer('chat')
	const actor = useChatActor()
	const { topics, reload: reloadTopics } = useConversations()
	const activeTopicId = useSelector(activeTopicId$)
	const { messages, reload: reloadThread } = useActiveThread(activeTopicId)

	useEffect(() => {
		if (initialTopicId && initialTopicId !== activeTopicId) {
			activeTopicId$.set(initialTopicId)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialTopicId])

	const { replyTo, clearReply } = useReplyTarget()
	const actions = useChatActions()
	const [membersOpen, setMembersOpen] = useState(false)

	const activeTopic = topics.find(t => t.id === activeTopicId) ?? null
	const fallback = content.unnamed.value
	const headerTitle = activeTopic ? topicTitle(activeTopic, fallback) : ''
	const isGroup = activeTopic?.type === 'grp'
	const [query, setQuery] = useState('')

	const handleSend = async (text: string): Promise<Message | null> => {
		const sent = await actions.send(activeTopicId ?? '', text, replyTo ?? undefined)
		if (sent) {
			clearReply()
			reloadThread()
			reloadTopics()
		}
		return sent
	}

	const openTopic = (id: string) => {
		activeTopicId$.set(id)
		replyingTo$.set(null)
		reloadTopics()
	}

	const filtered = query
		? topics.filter(t => topicTitle(t, fallback).toLowerCase().includes(query.toLowerCase()))
		: topics

	return (
		<div className='breakout'>
			<div className='flex h-[calc(100vh-4.65rem)] w-full overflow-hidden border border-border bg-background'>
				<aside className='flex w-80 shrink-0 flex-col border-r border-border bg-muted/20'>
					<div className='flex items-center justify-between gap-2 border-b border-border px-3 py-3'>
						<Input
							value={query}
							onChange={e => setQuery(e.target.value)}
							placeholder={content.searchConversations.value}
							className='h-9 bg-background'
						/>

						<NewConversationDialog
							onStartP2P={async peerId => {
								const topic = await actions.startP2P(peerId)
								if (topic) {
									openTopic(topic.id)
									return topic
								}
								return null
							}}
							onCreateGroup={async (name, memberIds) => {
								const topic = await actions.createGroup(name, memberIds)
								if (topic) {
									openTopic(topic.id)
									return topic
								}
								return null
							}}
						/>
					</div>
					<ConversationList topics={filtered} activeTopicId={activeTopicId} onSelect={openTopic} />
				</aside>

				<section className='flex flex-1 flex-col bg-background'>
					{activeTopic ? (
						<>
							<header className='flex items-center gap-3 border-b border-border px-4 py-3'>
								<div className='min-w-0 flex-1'>
									<p className='truncate text-sm font-semibold leading-tight'>{headerTitle}</p>
									{isGroup && <p className='text-xs text-muted-foreground'>{content.group}</p>}
								</div>
								{isGroup && (
									<Button
										variant='ghost'
										size='icon'
										onClick={() => setMembersOpen(true)}
										aria-label={content.members.value}
									>
										<Icons.MoreVertical size={18} />
									</Button>
								)}
							</header>

							<MessageThread messages={messages} currentUserId={actor.id} />
							<MessageComposer onSend={handleSend} replyTo={replyTo} onCancelReply={clearReply} />

							{isGroup && (
								<ConversationMembersSheet
									topicId={activeTopic.id}
									open={membersOpen}
									onOpenChange={setMembersOpen}
									onAdd={userId => actions.addMember(activeTopic.id, userId)}
									onRemove={userId => actions.removeMember(activeTopic.id, userId)}
								/>
							)}
						</>
					) : (
						<div className='flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center'>
							<div className='flex h-12 w-12 items-center justify-center rounded-md border border-border bg-muted'>
								<Icons.Email size={20} className='text-muted-foreground' />
							</div>
							<p className='text-sm text-muted-foreground'>{content.selectConversation}</p>
						</div>
					)}
				</section>
			</div>
		</div>
	)
}

export { ChatPage }
